interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class SessionCache {
  private cache: Map<string, CacheItem<any>> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  // Set cache item with TTL
  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl
    });
  }

  // Get cache item if not expired
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return item.data as T;
  }

  // Check if cache item exists and is not expired
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) {
      return false;
    }

    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  // Delete cache item
  delete(key: string): void {
    this.cache.delete(key);
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
  }

  // Clear expired items
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  // Get cache size
  size(): number {
    return this.cache.size;
  }

  // Get cache keys
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  // Set with custom expiration
  setWithExpiration<T>(key: string, data: T, expirationDate: Date): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: expirationDate.getTime()
    });
  }

  // Extend TTL for existing item
  extend(key: string, additionalTtl: number): boolean {
    const item = this.cache.get(key);
    if (!item) {
      return false;
    }

    item.expiresAt += additionalTtl;
    return true;
  }

  // Get cache statistics
  getStats(): {
    size: number;
    keys: string[];
    oldestItem?: number;
    newestItem?: number;
  } {
    const keys = this.keys();
    const timestamps = keys.map(key => this.cache.get(key)?.timestamp || 0);
    
    return {
      size: this.size(),
      keys,
      oldestItem: timestamps.length > 0 ? Math.min(...timestamps) : undefined,
      newestItem: timestamps.length > 0 ? Math.max(...timestamps) : undefined
    };
  }
}

// Create singleton instance
export const sessionCache = new SessionCache();

// Auto-cleanup every 10 minutes
setInterval(() => {
  sessionCache.cleanup();
}, 10 * 60 * 1000);

// Cache keys constants
export const CACHE_KEYS = {
  ORDERS: 'orders',
  APPOINTMENTS: 'appointments',
  NOTIFICATIONS: 'notifications',
  PROFILE: 'profile',
  TAILORS: 'tailors',
  MEASUREMENTS: 'measurements',
  MESSAGES: 'messages',
  CONVERSATIONS: 'conversations'
} as const;

// Helper functions for common cache operations
export const cacheHelpers = {
  // Cache orders with user-specific key
  setOrders: (userId: string, orders: any[], ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.ORDERS}_${userId}`, orders, ttl);
  },

  // Get cached orders
  getOrders: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.ORDERS}_${userId}`);
  },

  // Cache appointments
  setAppointments: (userId: string, appointments: any[], ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.APPOINTMENTS}_${userId}`, appointments, ttl);
  },

  // Get cached appointments
  getAppointments: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.APPOINTMENTS}_${userId}`);
  },

  // Cache notifications
  setNotifications: (userId: string, notifications: any[], ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.NOTIFICATIONS}_${userId}`, notifications, ttl);
  },

  // Get cached notifications
  getNotifications: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.NOTIFICATIONS}_${userId}`);
  },

  // Cache messages/conversations
  setMessages: (userId: string, messages: any[], ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.MESSAGES}_${userId}`, messages, ttl);
  },

  // Get cached messages
  getMessages: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.MESSAGES}_${userId}`);
  },

  // Cache tailors
  setTailors: (userId: string, tailors: any[], ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.TAILORS}_${userId}`, tailors, ttl);
  },

  // Get cached tailors
  getTailors: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.TAILORS}_${userId}`);
  },

  // Cache measurements
  setMeasurements: (userId: string, measurements: any[], ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.MEASUREMENTS}_${userId}`, measurements, ttl);
  },

  // Get cached measurements
  getMeasurements: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.MEASUREMENTS}_${userId}`);
  },

  // Cache profile
  setProfile: (userId: string, profile: any, ttl?: number) => {
    sessionCache.set(`${CACHE_KEYS.PROFILE}_${userId}`, profile, ttl);
  },

  // Get cached profile
  getProfile: (userId: string) => {
    return sessionCache.get(`${CACHE_KEYS.PROFILE}_${userId}`);
  },

  // Clear user-specific cache
  clearUserCache: (userId: string) => {
    const keys = sessionCache.keys();
    keys.forEach(key => {
      if (key.includes(userId)) {
        sessionCache.delete(key);
      }
    });
  }
};
